import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Pagination,
  Chip,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import {
  inventoryVendorsApi,
  InventoryVendor,
} from '../services/inventoryVendorsService.ts';

const emptyForm = {
  vendorName: '',
  accountNo: '',
  contactName: '',
  phone: '',
  email: '',
  paymentTerms: '',
  activeFlag: true,
};

const InventoryVendorsPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showError, showSuccess } = useSnackbar();
  const [vendors, setVendors] = useState<InventoryVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingVendor, setEditingVendor] = useState<InventoryVendor | null>(null);

  const fetchVendors = async (pageNum: number = 1, searchValue: string = search) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response = await inventoryVendorsApi.getVendors(
        { page: pageNum, limit, search: searchValue.trim() || undefined },
        sessionToken
      );
      setVendors(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch vendors';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchVendors(value);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchVendors(1, value), 500);
  };

  const handleOpenCreate = () => {
    setEditingVendor(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (vendor: InventoryVendor) => {
    setEditingVendor(vendor);
    setForm({
      vendorName: vendor.vendorName,
      accountNo: vendor.accountNo ?? '',
      contactName: vendor.contactName ?? '',
      phone: vendor.phone ?? '',
      email: vendor.email ?? '',
      paymentTerms: vendor.paymentTerms ?? '',
      activeFlag: vendor.activeFlag,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (vendor: InventoryVendor) => {
    if (!sessionToken) return;

    if (window.confirm(`Are you sure you want to delete "${vendor.vendorName}"?`)) {
      try {
        await inventoryVendorsApi.deleteVendor(vendor.id, sessionToken);
        showSuccess('Vendor deleted successfully!');
        fetchVendors(page);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete vendor';
        showError(errorMessage);
      }
    }
  };

  const handleCloseDialog = () => {
    if (dialogLoading) return;
    setDialogOpen(false);
  };

  const handleFieldChange = (field: keyof typeof emptyForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const canSave = !!form.vendorName.trim() && !dialogLoading;

  const handleSave = async () => {
    if (!sessionToken || !canSave) return;

    setDialogLoading(true);
    try {
      const payload = {
        vendorName: form.vendorName.trim(),
        accountNo: form.accountNo.trim() || undefined,
        contactName: form.contactName.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        paymentTerms: form.paymentTerms.trim() || undefined,
        activeFlag: form.activeFlag,
      };
      if (editingVendor) {
        await inventoryVendorsApi.updateVendor(editingVendor.id, payload, sessionToken);
        showSuccess('Vendor updated successfully!');
      } else {
        await inventoryVendorsApi.createVendor(payload, sessionToken);
        showSuccess('Vendor created successfully!');
      }
      setDialogOpen(false);
      fetchVendors(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save vendor';
      showError(errorMessage);
    } finally {
      setDialogLoading(false);
    }
  };

  const formatDate = (timestamp: string | number) => {
    const seconds = Number(timestamp);
    if (!seconds) return '-';
    return new Date(seconds * 1000).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Vendors</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => fetchVendors(page)} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create
          </Button>
        </Box>
      </Box>

      <TextField
        value={search}
        onChange={(e) => handleSearchChange(e.target.value)}
        placeholder="Search by vendor name, contact, or email"
        size="small"
        sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Vendor Name</TableCell>
              <TableCell>Account No</TableCell>
              <TableCell>Contact Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Payment Terms</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Created</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : vendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No vendors found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              vendors.map((vendor) => (
                <TableRow key={vendor.id} hover>
                  <TableCell>{vendor.id}</TableCell>
                  <TableCell>{vendor.vendorName}</TableCell>
                  <TableCell>{vendor.accountNo || '-'}</TableCell>
                  <TableCell>{vendor.contactName || '-'}</TableCell>
                  <TableCell>{vendor.phone || '-'}</TableCell>
                  <TableCell>{vendor.email || '-'}</TableCell>
                  <TableCell>{vendor.paymentTerms || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={vendor.activeFlag ? 'Active' : 'Inactive'}
                      color={vendor.activeFlag ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(vendor.createdDate)}</TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(vendor)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(vendor)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination page={page} count={totalPages} onChange={handlePageChange} color="primary" />
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
        {total} vendor{total === 1 ? '' : 's'} total
      </Typography>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingVendor ? 'Update Vendor' : 'Create Vendor'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vendor Name"
                value={form.vendorName}
                onChange={(e) => handleFieldChange('vendorName', e.target.value)}
                fullWidth
                required
                autoFocus
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Account No"
                value={form.accountNo}
                onChange={(e) => handleFieldChange('accountNo', e.target.value)}
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Name"
                value={form.contactName}
                onChange={(e) => handleFieldChange('contactName', e.target.value)}
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Terms"
                value={form.paymentTerms}
                onChange={(e) => handleFieldChange('paymentTerms', e.target.value)}
                placeholder="e.g. Net 30"
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.activeFlag}
                    onChange={(e) => handleFieldChange('activeFlag', e.target.checked)}
                    disabled={dialogLoading}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={dialogLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {dialogLoading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryVendorsPage;
